import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { Icon } from './Icon';

export interface ISelectionCardProps {
  selected: boolean;
  onClick: () => void;
  icon: string;
  title: string;
  description: string;
  animationDelay?: number;
}

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AnimatedWrapper = styled.div<{ animationDelay: number }>`
  width: 100%;
  animation: ${fadeSlideIn} 400ms ease-out;
  animation-delay: ${(props): string => `${props.animationDelay * 80}ms`};
  animation-fill-mode: both;
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Card = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 12px;
  border: ${(props): string =>
    props.$selected ? '2px solid var(--color-primary)' : '1px solid rgba(158, 158, 158, 0.3)'};
  border-radius: 8px;
  background-color: ${(props): string =>
    props.$selected ? 'rgba(66, 165, 245, 0.025)' : 'transparent'};
  cursor: pointer;
  transition: all 200ms ease-in-out;
  text-align: left;

  &:hover {
    border-color: ${(props): string =>
      props.$selected ? 'var(--color-primary)' : 'rgba(158, 158, 158, 0.5)'};
    background-color: ${(props): string =>
      props.$selected ? 'rgba(66, 165, 245, 0.05)' : 'rgba(158, 158, 158, 0.05)'};
  }

  &:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
`;

const IconWrapper = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
  margin-left: 12px;
  margin-right: 12px;
`;

const Title = styled.div<{ $selected: boolean }>`
  font-size: 14px;
  font-weight: ${(props): string => (props.$selected ? '600' : '400')};
  color: var(--color-text);
  margin-bottom: 2px;
`;

const Description = styled.div`
  font-size: 11px;
  color: #9e9e9e;
`;

const CheckIconWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const SelectionCard = React.memo(
  (props: ISelectionCardProps): React.ReactElement => {
    return (
      <AnimatedWrapper animationDelay={props.animationDelay ?? 0}>
        <Card
          type='button'
          $selected={props.selected}
          onClick={props.onClick}
          aria-pressed={props.selected}
        >
          <IconWrapper $selected={props.selected}>
            <Icon
              name={props.icon}
              size='medium'
              color={props.selected ? 'var(--color-primary)' : '#9e9e9e'}
            />
          </IconWrapper>
          <Content>
            <Title $selected={props.selected}>{props.title}</Title>
            <Description>{props.description}</Description>
          </Content>
          {props.selected && (
            <CheckIconWrapper>
              <Icon name='check_circle' size='small' color='var(--color-primary)' />
            </CheckIconWrapper>
          )}
        </Card>
      </AnimatedWrapper>
    );
  },
  (
    prevProps: Readonly<ISelectionCardProps>,
    nextProps: Readonly<ISelectionCardProps>
  ): boolean => {
    return (
      prevProps.selected === nextProps.selected &&
      prevProps.icon === nextProps.icon &&
      prevProps.title === nextProps.title &&
      prevProps.description === nextProps.description &&
      prevProps.onClick === nextProps.onClick &&
      prevProps.animationDelay === nextProps.animationDelay
    );
  }
);

SelectionCard.displayName = 'SelectionCard';
